import { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';

import authContext from '../../context/auth/authContext.js'
import { useTurnstile } from '../../presentation/hooks/useTurnstile.js';
import { useCaptchaVerification } from '../../presentation/hooks/useCaptchaVerification.js';

import { loginUseCase, posHasUsersUseCase } from '../../di/usecase_module';
import { sessionLocalRepo } from '../../di/repo_module';
import { LoginRequestDto } from '../../domain/usecases/session/LoginUseCase';

import { UIActions } from '../../presentation/redux/actions/UIActions';

import { LoadingScreen } from '../Layout/LoadingScreen';
import { Login2fa } from './Login_2fa';

import { sendEmailHelp } from '../action/login2fa/sendEmailHelp';
import { fetchLogoById, fetchLogoFromLocalStorage } from '../action/fetchLogoById';

import loadingImg from '../../img/img-loading3.gif'
import logo from './logo-final.svg';
import './login.css';

const Login = () => {
	const dispatch = useDispatch();
	const { isLoadingActive } = useSelector(state => state.uiState);
	const AuthContext = useContext(authContext);
	const { iniciarSesion } = AuthContext;

	const [pos, setPos] = useState(null);
	const [ureq, setUreq] = useState(false);
	const [tmpData, setTmpData] = useState({});
	const [message, setMessage] = useState(null);
	const [userPin, setUserPin] = useState(null);
	const [tempToken, setTempToken] = useState('');
	const [twofareq, setTwofareq] = useState(false);
	const [isPending, setIsPending] = useState(false);
	const [isShowing, setIsShowing] = useState(false);
	const [logoUrl, setLogoUrl] = useState(loadingImg);
	const [isDisabled, setIsDisabled] = useState(false);
	const [isRequired, setIsRequired] = useState(false);

	// Custom hook para Turnstile
	const {
		isScriptLoaded,
		captchaToken,
		btnDisabled,
		resetCaptcha
	} = useTurnstile();

	// Usar el hook para verificación
	const { verifyCaptcha } = useCaptchaVerification(setMessage, dispatch);

	var password = "";
	var code_2fa = "";
	let inUser = "";

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (isLoadingActive || btnDisabled) return;

		// setIsLoading(true);
		setMessage('');
		setIsRequired(false);

		if (!isScriptLoaded) {
			setMessage("El CAPTCHA no ha terminado de cargar. Por favor, espera un momento.");
			return;
		}

		if (!pos) {
			handlePointOfSaleInput();
			return;
		}

		dispatch(UIActions.uiLoadActive('Verificando CAPTCHA...'));

		const verificationSuccess = await verifyCaptcha(captchaToken, startLogin, resetCaptcha);

		if (!verificationSuccess) {
			dispatch(UIActions.uiLoadInactive());
		}
	};

	//Extraer parametro dst de la URL y almacenamiento local 
	let { search } = useLocation();
	let query = new URLSearchParams(search);
	let id = query.get("dst");
	let dst_id = localStorage.getItem("dst_Id");

	//useEffect para obtener y establecer la URL del logo según el ID de la URL o el almacenamiento local.
	useEffect(() => {
		const fetchLogo = async () => {
			try {
				if (id) {
					localStorage.setItem("dst_Id", id);
					const url = await fetchLogoById(id);
					if (url) {
						setLogoUrl(url);
					} else {
						setLogoUrl(logo);
					}
				} else if (!id && dst_id) {
					const url = await fetchLogoFromLocalStorage(dst_id);
					if (url) {
						setLogoUrl(url);
					} else {
						setLogoUrl(logo);
					}
				} else {
					setLogoUrl(logo);
				}
			} catch (error) {
				setLogoUrl(logo);
			}
		};

		fetchLogo();
	}, [id, dst_id]);

	//Proceso de inicio de sesión del usuario, valida el punto de venta (POS) y formulario, procesa autenticación llamando al inicio de sesión.
	//Si tiene pin usuarios y si es ingresado un valor actualiza el estado.
	const startLogin = () => {
		if (!pos) {
			handlePointOfSaleInput();
			dispatch(UIActions.uiLoadInactive());
			return;
		}
		if (!isFormValid()) {
			dispatch(UIActions.uiLoadInactive());
			return;
		}

		if (ureq && document.getElementById("in-user")?.value.trim()) {
			const userInput = document.getElementById("in-user").value;
			setUserPin(userInput.trim());
			inUser = document.getElementById("in-user").value.trim();
		}

		loginUseCase()
			.execute(createLoginRequest())
			.then(handleLoginSuccess)
			.catch(handleLoginError)
			.finally(() => {
				// Resetear el CAPTCHA después de cada intento
				resetCaptcha();
			});
	};

	// Valida y procesa el punto de venta (POS) ingresado. Verifica con `posHasUsersUseCase` si el punto de venta tiene pin usuarios asociados.
	const handlePointOfSaleInput = () => {
		const pointOfSale = document.getElementById("in-puntoVenta").value.trim();

		if (pointOfSale.length === 0 || pointOfSale.length !== 9) {
			setMessage('Favor de introducir punto de venta');
			setIsRequired(true);
			return;
		}

		dispatch(UIActions.uiLoadActive());
		posHasUsersUseCase()
			.execute(pointOfSale)
			.then((hasUsers) => {
				setUreq(hasUsers.pos_has_users);
				setPos(pointOfSale);
			})
			.catch((e) => {
				setMessage(e.message ?? 'Ocurrió un error inesperado. Por favor verifica tu conexión.');
			})
			.finally(() => {
				dispatch(UIActions.uiLoadInactive());
			});
	};

	// Retorna objeto de solicitud de inicio de sesión (LoginRequestDto) con la información necesaria.
	const createLoginRequest = () => {
		return new LoginRequestDto(
			pos,
			encodeURIComponent(password),
			ureq ? inUser : undefined,
			twofareq ? code_2fa?.trim() : undefined
		);
	};

	// Maneja el flujo de inicio de sesión exitoso, valida si es necesario evaluar la autenticación de dos factores (2FA).
	const handleLoginSuccess = (data) => {
		if (data.evalua_2fa) {
			handleTwoFactorAuthentication(data);
		} else {
			completeLogin(data);
		}
	};

	// Gestiona el flujo para la autenticación de dos factores (2FA).
	const handleTwoFactorAuthentication = (data) => {
		setTwofareq(data.evalua_2fa);
		setTempToken(data.token);
		setMessage(`Hemos enviado el código de verificación a ${data.email}`);
		setTmpData(data);
		dispatch(UIActions.uiLoadInactive());
	};

	// Completa el proceso de inicio de sesión exitoso.
	const completeLogin = (data) => {
		sessionLocalRepo().setSession(data);
		iniciarSesion(data);
		dispatch(UIActions.uiLoadInactive());
	};

	// Maneja los errores que ocurren durante el proceso de inicio de sesión.
	const handleLoginError = (e) => {
		if (e.code === 'PENDING_VERIFY_2FA') {
			setIsPending(true);
			setMessage(e.message);
		} else {
			sessionLocalRepo().clearSession();
			setMessage(e.message);
		}
		dispatch(UIActions.uiLoadInactive());
	};

	// Valida si el formulario de inicio de sesión está correctamente completado.
	const isFormValid = () => {
		let isValid = true;
		password = document.getElementById("in-password").value;
		if (password.trim().length === 0) {
			setMessage('Favor de introducir la contraseña');
			setIsRequired(true);
			isValid = false;
		}
		return isValid;
	}

	// Envía una solicitud de ayuda por correo electrónico.
	const sendHelp = () => {
		const data = {
			puntoVenta: pos,
			uid: userPin
		};
		dispatch(sendEmailHelp(data));
		setIsDisabled(!isDisabled);
	}

	return (
		<>
			{isLoadingActive &&
				<LoadingScreen />
			}
			<div className="login-container justify-content-center  align-items-center  d-flex flex-column body-gradient"
				style={{ top: '0px', bottom: '0px', left: '0px', right: '0px', position: 'fixed' }}>
				<div className="miclase col-lg-4 col-md-6 col-sm-12 bg-white shadow p-3">
					<form>
						{(message) &&
							<Alert className='text-justify' variant="warning" onClose={() => setMessage(null)} dismissible>
								{message}
							</Alert>
						}
						<div className="row justify-content-center py-1">
							<img style={{ width: '100%', maxWidth: '12rem', height: 'auto', maxHeight: '10rem' }} src={logoUrl} alt="logo redsmart" />
						</div>
						{
							(twofareq) ?
								<Login2fa
									tempToken={tempToken}
									setMessage={setMessage}
									isRequired={isRequired}
									setIsRequired={setIsRequired}
									dispatch={dispatch}
									tmpData={tmpData}
								/> :
								<>
									<div className="login-pdv text-center">
										<h2>Iniciar Sesión</h2>
									</div>
									{
										(pos) && <div className="text-center text-muted">
											<span>POS: <b>{pos}</b></span>
										</div>
									}
									{
										(!pos) && <div className="row justify-content-center py-3">
											<input
												autoFocus
												type="text"
												className={`col-10 ${isRequired ? 'required' : ''}`}
												name="in-puntoVenta"
												id="in-puntoVenta"
												autoComplete='off'
												maxLength={9}
												placeholder="Ingresa Punto de Venta" />
										</div>
									}
									{
										(pos) && <div className="row justify-content-center py-1">
											<div className="col-10">
												<input
													type={(isShowing) ? `text` : `password`}
													autoComplete="off"
													className={(isRequired && password.trim().length === 0) ? "required" : ""}
													name="in-password"
													id="in-password"
													maxLength={80}
													placeholder="Contraseña"
												/>
												<button type='button' className='btn-show'
													onClick={() => { setIsShowing(!isShowing) }}>
													<i className={(isShowing) ? `fas fa-eye` : `fas fa-eye-slash`}></i>
												</button>
											</div>
										</div>
									}
									{
										(ureq) && <div className="row justify-content-center py-3">
											<input
												type="text"
												className="col-10"
												name="in-user"
												id="in-user"
												autoComplete='off'
												maxLength={12}
												defaultValue={userPin || ''}
												placeholder="Id Pin Usuario" />
										</div>
									}
									<div className="row justify-content-center py-3">
										<button
											type="submit"
											className={`btn col-10 ${!isScriptLoaded ? 'disabled' : ''}`}
											disabled={!isScriptLoaded}
											id="logIn" onClick={handleSubmit}>{pos ? 'Entrar' : 'Siguiente'}</button>
									</div>
									<div className='text-center'>
										<div
											id="my-turnstile-widget"
											style={{ minHeight: '65px' }} // Altura mínima para evitar layout shift
										>
										</div>
									</div>
								</>
						}
						{
							(pos) &&
							<div className="justify-content-center text-center">
								<Button variant="link" onClick={(_) => {
									setUreq(false);
									setTwofareq(false);
									setPos(null);
									setMessage('');
									setIsRequired(false);
									setIsShowing(false);
									setIsDisabled(false);
									setIsPending(false);
									setUserPin(null);
									resetCaptcha();
								}} >Regresar</Button>
							</div>
						}
					</form>
					{
						(isPending) &&
						<div className='text-center'>
							<button type="button" className="btn col-10 mt-1" id="sendHelp" disabled={isDisabled} onClick={sendHelp} >
								<small>¿Necesitas ayuda?</small>
							</button>
						</div>
					}
					{
						(twofareq === false && !isPending) &&
						<div className='text-center'>
							<NavLink to="/passrecovery">
								<small>¿Olvidaste tu contraseña?</small>
							</NavLink>
						</div>
					}
				</div>
			</div>
		</>);
}

export default Login;